/**
 * POS Products - Product management for Point of Sale
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Plus, Search, Edit2, Trash2, ArrowLeft,
  Filter, MoreVertical, AlertTriangle, Barcode, X,
  ChevronDown, Check, Download, Upload, History
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { posAPI } from '../../services/api';
import { toast } from 'sonner';

// Product Search Input with autocomplete
const ProductSearchInput = ({ products, value, onChange, placeholder, showStock = false }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync selected product when value prop changes (controlled component pattern)
  useEffect(() => {
    if (value) {
      const product = products.find(p => p.id === value);
      if (product && selectedProduct?.id !== product.id) {
        setSelectedProduct(product);
        setSearch(product.name);
      }
    } else if (selectedProduct) {
      setSelectedProduct(null);
      setSearch('');
    }
  }, [value, products]); // Intentionally excluding selectedProduct to avoid loops

  // Filter products based on search term (name, barcode, sku, supplier)
  const filteredProducts = products.filter(p => {
    // If no search term, show all active products
    if (!search || search.length === 0) return p.is_active !== false;
    const searchLower = search.toLowerCase();
    return p.is_active !== false && (
      p.name?.toLowerCase().includes(searchLower) ||
      p.barcode?.toLowerCase().includes(searchLower) ||
      p.sku?.toLowerCase().includes(searchLower) ||
      p.supplier_name?.toLowerCase().includes(searchLower)
    );
  }).slice(0, 8); // Limit results

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (product) => {
    setSelectedProduct(product);
    setSearch(product.name);
    setIsOpen(false);
    onChange(product.id, product);
  };

  const handleClear = () => {
    setSelectedProduct(null);
    setSearch('');
    onChange('', null);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
            if (!e.target.value) {
              setSelectedProduct(null);
              onChange('', null);
            }
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder || "Buscar producto..."}
          className="w-full h-9 pl-8 pr-8 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {selectedProduct && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-[100] w-full mt-1 bg-card border border-border rounded-lg shadow-xl max-h-48 overflow-y-auto"
        >
          {filteredProducts.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              No se encontraron productos
            </div>
          ) : (
            filteredProducts.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => handleSelect(product)}
                className={`w-full px-3 py-2 text-left hover:bg-secondary/50 transition-colors flex items-center justify-between ${
                  selectedProduct?.id === product.id ? 'bg-primary/10' : ''
                }`}
              >
                <div>
                  <p className="text-sm font-medium">{product.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {product.barcode && `${product.barcode} • `}
                    {product.supplier_name && `${product.supplier_name} • `}
                    ${product.sale_price?.toFixed(2)}
                  </p>
                </div>
                {showStock && (
                  <span className={`text-xs px-2 py-1 rounded ${
                    product.stock > 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                  }`}>
                    Stock: {product.stock}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Product form modal
const ProductModal = ({ product, departments, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState(product || {
    name: '',
    description: '',
    sku: '',
    barcode: '',
    department_id: '',
    category_id: '',
    purchase_price: 0,
    sale_price: 0,
    has_iva: true,
    stock: 0,
    min_stock: 5,
    supplier_name: '',
    supplier_contact: '',
  });
  const [saving, setSaving] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);

  useEffect(() => {
    if (formData.department_id) {
      setFilteredCategories(categories.filter(c => c.department_id === formData.department_id));
    } else {
      setFilteredCategories([]);
    }
  }, [formData.department_id, categories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category_id || !formData.sale_price) {
      toast.error('Completa los campos requeridos');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-card rounded-2xl border border-border shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">
            {product ? 'Editar Producto' : 'Nuevo Producto'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del producto"
                required
              />
            </div>
            
            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional"
              />
            </div>
          </div>

          {/* Categorization */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Departamento *</Label>
              <select
                value={formData.department_id}
                onChange={(e) => setFormData({ ...formData, department_id: e.target.value, category_id: '' })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                required
              >
                <option value="">Seleccionar...</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Categoría *</Label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                required
                disabled={!formData.department_id}
              >
                <option value="">Seleccionar...</option>
                {filteredCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Codes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>SKU</Label>
              <Input
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                placeholder="Ej: COCA600"
              />
            </div>
            <div>
              <Label>Código de barras</Label>
              <Input
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                placeholder="Escanear o escribir"
              />
            </div>
          </div>

          {/* Prices */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Precio compra</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.purchase_price}
                onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Precio venta *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.sale_price}
                onChange={(e) => setFormData({ ...formData, sale_price: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>
            <div>
              <Label>IVA</Label>
              <div className="flex items-center gap-2 h-10">
                <input
                  type="checkbox"
                  checked={formData.has_iva}
                  onChange={(e) => setFormData({ ...formData, has_iva: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm">Aplica IVA</span>
              </div>
            </div>
          </div>

          {/* Stock */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Stock actual</Label>
              <Input
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Stock mínimo</Label>
              <Input
                type="number"
                min="0"
                value={formData.min_stock}
                onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          {/* Supplier */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Proveedor</Label>
              <Input
                value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                placeholder="Nombre del proveedor"
              />
            </div>
            <div>
              <Label>Contacto proveedor</Label>
              <Input
                value={formData.supplier_contact}
                onChange={(e) => setFormData({ ...formData, supplier_contact: e.target.value })}
                placeholder="Teléfono o email"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : product ? 'Actualizar' : 'Crear Producto'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Stock adjustment modal
const StockModal = ({ product, onClose, onSave }) => {
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState('purchase');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (quantity === 0) {
      toast.error('La cantidad no puede ser 0');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({ quantity, reason, notes });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold">Ajustar Stock</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="text-center p-3 bg-secondary rounded-lg">
            <p className="text-sm text-muted-foreground">Stock actual de</p>
            <p className="font-bold text-lg">{product.name}</p>
            <p className="text-2xl font-display font-bold text-primary">{product.stock}</p>
          </div>

          <div>
            <Label>Cantidad (+ agregar, - restar)</Label>
            <Input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
              placeholder="Ej: 10 o -5"
            />
            {quantity !== 0 && (
              <p className="text-sm mt-1 text-muted-foreground">
                Nuevo stock: <span className="font-bold text-foreground">{product.stock + quantity}</span>
              </p>
            )}
          </div>

          <div>
            <Label>Razón</Label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="purchase">Compra/Reabastecimiento</option>
              <option value="adjustment">Ajuste de inventario</option>
              <option value="damage">Daño/Pérdida</option>
              <option value="return">Devolución</option>
              <option value="transfer">Transferencia</option>
            </select>
          </div>

          <div>
            <Label>Notas (opcional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas adicionales..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              {saving ? 'Guardando...' : 'Ajustar Stock'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Inventory Entry Modal (Compra / Donación)
const InventoryEntryModal = ({ products, onClose, onSave }) => {
  const [entryType, setEntryType] = useState('compra');
  const [supplierOrDonor, setSupplierOrDonor] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ product_id: '', product_name: '', quantity: 1, unit_cost: 0, total: 0 }]);
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    setItems([...items, { product_id: '', product_name: '', quantity: 1, unit_cost: 0, total: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-calculate total
    if (field === 'quantity' || field === 'unit_cost') {
      newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].unit_cost || 0);
    }
    
    setItems(newItems);
  };

  const grandTotal = items.reduce((sum, item) => sum + (item.total || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!supplierOrDonor.trim()) {
      toast.error(entryType === 'compra' ? 'Ingresa el proveedor' : 'Ingresa el origen');
      return;
    }
    
    const validItems = items.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        entry_type: entryType,
        supplier_or_donor: supplierOrDonor,
        invoice_number: invoiceNumber || undefined,
        items: validItems,
        notes: notes || undefined
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-card rounded-2xl border border-border shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-4 border-b border-border flex items-center justify-between bg-green-500/10">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-green-500" />
            <h2 className="font-display font-bold text-lg">Entrada de Inventario</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Entry Type */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEntryType('compra')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                entryType === 'compra' ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <p className="font-medium">Compra</p>
              <p className="text-xs text-muted-foreground">Adquisición de mercancía</p>
            </button>
            <button
              type="button"
              onClick={() => setEntryType('donacion')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                entryType === 'donacion' ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <p className="font-medium">Donación/Traspaso</p>
              <p className="text-xs text-muted-foreground">Recepción sin costo</p>
            </button>
          </div>

          {/* Supplier/Donor Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{entryType === 'compra' ? 'Proveedor *' : 'Origen *'}</Label>
              <Input
                value={supplierOrDonor}
                onChange={(e) => setSupplierOrDonor(e.target.value)}
                placeholder={entryType === 'compra' ? 'Nombre del proveedor' : 'Donante o sucursal de origen'}
                required
              />
            </div>
            <div>
              <Label>N° Factura/Documento</Label>
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Ej: FAC-001234"
              />
            </div>
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Productos</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
            
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 p-2 bg-secondary/50 rounded-lg items-end">
                  <div className="col-span-4 relative">
                    <Label className="text-xs">Producto</Label>
                    <ProductSearchInput
                      products={products}
                      value={item.product_id}
                      onChange={(productId, product) => {
                        updateItem(index, 'product_id', productId);
                        if (product) {
                          updateItem(index, 'product_name', product.name);
                          updateItem(index, 'unit_cost', product.purchase_price || 0);
                        }
                      }}
                      placeholder="Buscar por nombre, código..."
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Cantidad</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Costo Unit.</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_cost}
                      onChange={(e) => updateItem(index, 'unit_cost', parseFloat(e.target.value) || 0)}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Total</Label>
                    <div className="h-9 flex items-center px-2 bg-secondary rounded-md font-medium">
                      ${item.total.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-destructive"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grand Total */}
          <div className="p-3 bg-primary/10 rounded-lg flex justify-between items-center">
            <span className="font-medium">Total de la entrada:</span>
            <span className="text-xl font-bold text-primary">${grandTotal.toFixed(2)}</span>
          </div>

          {/* Notes */}
          <div>
            <Label>Notas (opcional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones adicionales..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" disabled={saving}>
              {saving ? 'Registrando...' : 'Registrar Entrada'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Inventory Exit Modal (Traspaso / Ajuste)
const InventoryExitModal = ({ products, onClose, onSave }) => {
  const [exitType, setExitType] = useState('traspaso');
  const [destinationBranch, setDestinationBranch] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('faltante');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ product_id: '', product_name: '', quantity: 1, unit_cost: 0, max_stock: 0 }]);
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    setItems([...items, { product_id: '', product_name: '', quantity: 1, unit_cost: 0, max_stock: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (exitType === 'traspaso' && !destinationBranch.trim()) {
      toast.error('Ingresa la sucursal destino');
      return;
    }
    
    const validItems = items.filter(item => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }
    
    // Validate stock availability
    for (const item of validItems) {
      if (item.quantity > item.max_stock) {
        toast.error(`Stock insuficiente para "${item.product_name}"`);
        return;
      }
    }
    
    setSaving(true);
    try {
      await onSave({
        exit_type: exitType,
        destination_branch: exitType === 'traspaso' ? destinationBranch : undefined,
        adjustment_reason: exitType === 'ajuste' ? adjustmentReason : undefined,
        items: validItems.map(({ product_id, product_name, quantity, unit_cost }) => ({
          product_id, product_name, quantity, unit_cost
        })),
        notes: notes || undefined
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-card rounded-2xl border border-border shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="p-4 border-b border-border flex items-center justify-between bg-orange-500/10">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-orange-500" />
            <h2 className="font-display font-bold text-lg">Salida de Inventario</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Exit Type */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setExitType('traspaso')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                exitType === 'traspaso' ? 'border-orange-500 bg-orange-500/10' : 'border-border'
              }`}
            >
              <p className="font-medium">Traspaso</p>
              <p className="text-xs text-muted-foreground">Envío a otra sucursal</p>
            </button>
            <button
              type="button"
              onClick={() => setExitType('ajuste')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                exitType === 'ajuste' ? 'border-orange-500 bg-orange-500/10' : 'border-border'
              }`}
            >
              <p className="font-medium">Ajuste</p>
              <p className="text-xs text-muted-foreground">Merma, robo, vencimiento</p>
            </button>
          </div>

          {/* Conditional Fields */}
          {exitType === 'traspaso' ? (
            <div>
              <Label>Sucursal Destino *</Label>
              <Input
                value={destinationBranch}
                onChange={(e) => setDestinationBranch(e.target.value)}
                placeholder="Nombre de la sucursal destino"
                required
              />
            </div>
          ) : (
            <div>
              <Label>Motivo del Ajuste *</Label>
              <select
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="merma">Merma</option>
                <option value="robo">Robo</option>
                <option value="vencimiento">Vencimiento</option>
                <option value="faltante">Faltante de inventario</option>
              </select>
            </div>
          )}

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Productos</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
            
            <div className="space-y-2">
              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 p-2 bg-secondary/50 rounded-lg items-end">
                  <div className="col-span-6 relative">
                    <Label className="text-xs">Producto</Label>
                    <ProductSearchInput
                      products={products.filter(p => p.stock > 0)}
                      value={item.product_id}
                      showStock={true}
                      onChange={(productId, product) => {
                        updateItem(index, 'product_id', productId);
                        if (product) {
                          updateItem(index, 'product_name', product.name);
                          updateItem(index, 'unit_cost', product.avg_purchase_price || product.purchase_price || 0);
                          updateItem(index, 'max_stock', product.stock || 0);
                          // Adjust quantity if exceeds new max stock
                          if (items[index].quantity > product.stock) {
                            updateItem(index, 'quantity', product.stock);
                          }
                        }
                      }}
                      placeholder="Buscar por nombre, código..."
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Cantidad (máx: {item.max_stock})</Label>
                    <Input
                      type="number"
                      min="1"
                      max={item.max_stock}
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', Math.min(parseInt(e.target.value) || 0, item.max_stock))}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Costo Unit.</Label>
                    <div className="h-9 flex items-center px-2 bg-secondary rounded-md text-sm">
                      ${item.unit_cost.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-destructive"
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notas (opcional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones adicionales..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700" disabled={saving}>
              {saving ? 'Registrando...' : 'Registrar Salida'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default function POSProducts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockProduct, setStockProduct] = useState(null);
  // New states for inventory entry/exit
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsRes, deptsRes, catsRes] = await Promise.all([
        posAPI.getProducts({ active_only: false }),
        posAPI.getDepartments(),
        posAPI.getCategories()
      ]);
      
      setProducts(productsRes.data.products || []);
      setDepartments(deptsRes.data.departments || []);
      setCategories(catsRes.data.categories || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      toast.error('Error cargando productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = async (formData) => {
    try {
      if (editingProduct) {
        await posAPI.updateProduct(editingProduct.id, formData);
        toast.success('Producto actualizado');
      } else {
        await posAPI.createProduct(formData);
        toast.success('Producto creado');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando producto');
      throw err;
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`¿Eliminar "${product.name}"?`)) return;
    
    try {
      await posAPI.deleteProduct(product.id);
      toast.success('Producto eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error eliminando producto');
    }
  };

  // Handle inventory entry (Compra / Donación)
  const handleInventoryEntry = async (data) => {
    try {
      await posAPI.createInventoryEntry(data);
      toast.success('Entrada de inventario registrada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error registrando entrada');
      throw err;
    }
  };

  // Handle inventory exit (Traspaso / Ajuste)
  const handleInventoryExit = async (data) => {
    try {
      await posAPI.createInventoryExit(data);
      toast.success('Salida de inventario registrada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error registrando salida');
      throw err;
    }
  };

  const handleStockAdjustment = async (data) => {
    try {
      await posAPI.adjustStock(stockProduct.id, data);
      toast.success('Stock ajustado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error ajustando stock');
      throw err;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch = !searchTerm || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.barcode?.includes(searchTerm);
    const matchesDept = !selectedDept || p.department_id === selectedDept;
    return matchesSearch && matchesDept;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Productos</h1>
              <p className="text-xs text-muted-foreground">{products.length} productos</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowEntryModal(true)}
              className="border-green-500/50 text-green-500 hover:bg-green-500/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Entrada
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowExitModal(true)}
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            >
              <Upload className="w-4 h-4 mr-2" />
              Salida
            </Button>
            <Button onClick={() => { setEditingProduct(null); setShowProductModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Nuevo
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar producto, SKU o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[150px]"
          >
            <option value="">Todos los departamentos</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Cargando productos...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No hay productos</p>
            <Button 
              onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primer producto
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filteredProducts.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`glass rounded-xl p-4 ${!product.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{product.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {product.category?.name || 'Sin categoría'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => handleDeleteProduct(product)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  {product.sku && (
                    <span className="bg-secondary px-2 py-0.5 rounded">SKU: {product.sku}</span>
                  )}
                  {product.barcode && (
                    <span className="bg-secondary px-2 py-0.5 rounded flex items-center gap-1">
                      <Barcode className="w-3 h-3" />
                      {product.barcode.slice(-6)}
                    </span>
                  )}
                </div>

                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xl font-bold font-display">{formatCurrency(product.sale_price)}</p>
                    {product.purchase_price > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Costo: {formatCurrency(product.purchase_price)}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <button
                      onClick={() => setStockProduct(product)}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        product.stock <= (product.min_stock || 0)
                          ? 'bg-yellow-500/20 text-yellow-500'
                          : 'bg-green-500/20 text-green-500'
                      }`}
                    >
                      Stock: {product.stock}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showProductModal && (
          <ProductModal
            product={editingProduct}
            departments={departments}
            categories={categories}
            onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
            onSave={handleSaveProduct}
          />
        )}
        {stockProduct && (
          <StockModal
            product={stockProduct}
            onClose={() => setStockProduct(null)}
            onSave={handleStockAdjustment}
          />
        )}
        {showEntryModal && (
          <InventoryEntryModal
            products={products}
            onClose={() => setShowEntryModal(false)}
            onSave={handleInventoryEntry}
          />
        )}
        {showExitModal && (
          <InventoryExitModal
            products={products}
            onClose={() => setShowExitModal(false)}
            onSave={handleInventoryExit}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
